<template>
  <!-- SUPER_ADMIN: one location's card payments at the till and the kiosk. Per LOCATION, not per terminal — the setting
       is on the location document, so every till and kiosk there follows it (CagistaServices VivaTillPaymentService). -->
  <div class="tp-card">
    <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

    <div class="tp-head">
      <span class="fw-semibold">{{ location.name }}</span>
      <span v-if="location.shortName" class="tp-muted">({{ location.shortName }})</span>
    </div>

    <div v-if="loading" class="tp-muted mt-2">Henter…</div>
    <div v-else-if="loadError" class="tp-warn mt-2">{{ loadError }}</div>

    <template v-else>
      <table class="tp-table mt-2">
        <tbody>
          <tr>
            <td class="tp-label">Kasser{{ countText(s.terminals && s.terminals.tills) }}</td>
            <!-- None there: nothing pays with anything, whatever the location document says. -->
            <td><strong v-if="tills !== 0">{{ providerLabel(s.tillProvider) }}</strong><span v-else class="tp-muted">—</span></td>
          </tr>
          <tr>
            <td class="tp-label">Kiosker{{ countText(s.terminals && s.terminals.kiosks) }}</td>
            <td><strong v-if="kiosks !== 0">{{ providerLabel(s.kioskProvider) }}</strong><span v-else class="tp-muted">—</span></td>
          </tr>
          <tr>
            <td class="tp-label">VIVA-oppsett</td>
            <td>
              <template v-if="s.viva">
                <span :class="s.viva.current ? 'tp-ok' : 'tp-warn'">{{ s.viva.current ? 'På plass' : 'Utdatert' }}</span>
                <span class="tp-muted"> · {{ s.viva.environment || 'uten miljø' }}<span v-if="s.setUpTime">
                  · laget {{ formatTime(s.setUpTime) }} av {{ s.setUpBy }}</span></span>
              </template>
              <span v-else class="tp-muted">Ikke laget</span>
            </td>
          </tr>
          <tr v-if="s.switchedTime || s.backTime">
            <td class="tp-label">Historikk</td>
            <td class="tp-muted">
              <div v-if="s.switchedTime">Kassene byttet til VIVA {{ formatTime(s.switchedTime) }} av {{ s.switchedBy }}</div>
              <div v-if="s.backTime">Tilbake til Softpay {{ formatTime(s.backTime) }} av {{ s.backBy }}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Why a button is not offered, in the order they have to be fixed. -->
      <div v-if="!s.connected" class="tp-warn mt-2">
        Avdelingen er ikke koblet til CAGISTA hos VIVA ennå. Fullfør VIVA-onboardingen først (Innstillinger → Aktiver
        VIVA-betaling) — uten koblingen kan ikke CAGISTA refundere kassebetalingene.
      </div>
      <div v-else-if="s.config && !s.config.ready" class="tp-warn mt-2">
        VIVA Partner Config mangler ISV Client ID eller secret (System → Config → VIVA Partner Config).
      </div>
      <div v-if="s.viva && !s.viva.current && s.connected" class="tp-warn mt-2">
        Oppsettet er ikke likt VIVA Partner Config lenger — annen ISV-nøkkel, annet miljø eller mangler noe. Trykk
        «Oppdater VIVA-oppsett».
      </div>
      <div v-if="s.config && s.config.environment === 'DEMO'" class="tp-warn mt-2">
        VIVA Partner Config peker mot VIVAs demo: kassene åpner VIVAs demo-app, og ingen kort blir belastet.
      </div>

      <div class="d-flex flex-wrap mt-3" style="gap:0.5rem">
        <button v-if="canSetUp" class="bo_btn" :disabled="busy" @click="setUp">
          {{ s.viva ? 'Oppdater VIVA-oppsett' : 'Lag VIVA-oppsett' }}
        </button>
        <button v-if="canSwitch" class="bo_btn_success" :disabled="busy" @click="switchToViva">
          Bytt kassene til VIVA nå
        </button>
        <button v-if="canGoBack" class="bo_btn_secondary" :disabled="busy" @click="backToSoftpay">
          Tilbake til Softpay
        </button>
      </div>
      <div v-if="message" class="tp-warn mt-2">{{ message }}</div>

      <!-- What the server cannot check: the VIVA app on each device, and in which account it was activated. -->
      <div v-if="s.hasSoftpay && s.viva && s.viva.current" class="tp-note mt-3">
        <strong>Før byttet:</strong> TastyPOS 1.6.32 eller nyere på hver kasse, og VIVAs terminal-app («Viva.com |
        Terminal») installert og aktivert i <strong>restaurantens</strong> VIVA-konto — sjekk at kontovelgeren der viser
        restaurantens firma, ikke Cagista AS. Bytt om morgenen etter Z-rapporten; dagens Softpay-salg kan ikke refunderes i
        kassen etter byttet.
      </div>
      <div v-if="s.tillProvider === 'VIVA' && s.softpayKept" class="tp-note mt-3">
        Ta et kortkjøp på minst kr 10 på hver kasse: VIVA-appen åpner seg og går tilbake til kassen, og i ISV-kontoen står
        kilden «CAGISTA POS and KIOSK». Går det ikke, trykk «Tilbake til Softpay».
      </div>
    </template>
  </div>
</template>

<script>
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import VivaOnboardingService from "@/services/site/VivaOnboardingService";

export default {
  name: "TillPaymentPanel",
  components: { ConfirmDialogue },
  props: {
    location: { type: Object, required: true }   // { id, name, shortName }
  },
  data() {
    return {
      loading: true,
      loadError: '',
      busy: false,
      message: '',
      s: {}
    };
  },
  computed: {
    ready() {
      return !!(this.s.connected && this.s.config && this.s.config.ready);
    },
    canSetUp() {
      return this.ready && (!this.s.viva || !this.s.viva.current);
    },
    canSwitch() {
      return this.ready && this.s.hasSoftpay && this.s.viva && this.s.viva.current;
    },
    canGoBack() {
      return !this.s.hasSoftpay && this.s.softpayKept;
    },
    /** -1 when the count could not be read: then the provider is shown, never a dash on a guess. */
    tills() {
      return this.s.terminals ? this.s.terminals.tills : -1;
    },
    kiosks() {
      return this.s.terminals ? this.s.terminals.kiosks : -1;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.loadError = '';
      VivaOnboardingService.getTillStatus(this.location.id).then(
        (res) => {
          this.s = res.data || {};
          if (this.s.error) this.loadError = this.s.error;
          this.loading = false;
        },
        (err) => {
          console.log(err);
          this.loadError = 'Kunne ikke hente kortbetalingen for avdelingen.';
          this.loading = false;
        }
      );
    },
    providerLabel(p) {
      return { SOFTPAY: 'Softpay', VIVA: 'VIVA' }[p] || 'Ingen kortbetaling';
    },
    countText(n) {
      return typeof n === 'number' && n >= 0 ? ' (' + n + ')' : '';
    },
    async setUp() {
      // What happens at once depends on what is there: kiosks take VIVA as soon as the setup exists; tills only once
      // Softpay is gone — and a location without Softpay has nothing to wait for.
      let message;
      if (!this.s.hasSoftpay) {
        message = 'Avdelingen har ikke Softpay, så kassene og kioskene på ' + this.location.name
          + ' tar kort med VIVA så snart oppsettet er lagret.';
      } else {
        message = 'Kassene på ' + this.location.name + ' fortsetter med Softpay til du trykker «Bytt kassene til VIVA nå».';
        if (this.kiosks > 0) {
          message += ' Kioskene (' + this.kiosks + ') bytter til VIVA med en gang — de bruker VIVA så snart oppsettet finnes.';
        }
      }
      message += ' Oppsettet lages fra VIVA Partner Config; restaurantens egen VIVA-nøkkel trengs ikke.';
      const ok = await this.$refs.confirmDialogue.show({
        title: this.s.viva ? 'Oppdater VIVA-oppsett' : 'Lag VIVA-oppsett',
        message: message,
        okButton: this.s.viva ? 'Oppdater' : 'Lag oppsett',
        cancelButton: 'Avbryt'
      });
      if (ok) this.run(VivaOnboardingService.tillSetUp(this.location.id));
    },
    async switchToViva() {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Bytt kassene til VIVA',
        message: 'Kassene på ' + this.location.name + ' tar kort med VIVA fra nå, innen noen sekunder. Er VIVA-appen '
          + 'installert og aktivert på hver kasse, i restaurantens VIVA-konto? Er det etter Z-rapporten? '
          + 'Softpay-oppsettet tas vare på, og «Tilbake til Softpay» legger det tilbake.',
        okButton: 'Bytt nå',
        cancelButton: 'Avbryt'
      });
      if (ok) this.run(VivaOnboardingService.tillSwitchToViva(this.location.id));
    },
    async backToSoftpay() {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Tilbake til Softpay',
        message: 'Kassene på ' + this.location.name + ' tar kort med Softpay igjen, innen noen sekunder. '
          + 'VIVA-oppsettet blir liggende, så kioskene fortsetter med VIVA.',
        okButton: 'Tilbake til Softpay',
        cancelButton: 'Avbryt'
      });
      if (ok) this.run(VivaOnboardingService.tillBackToSoftpay(this.location.id));
    },
    run(request) {
      this.busy = true;
      this.message = '';
      request.then(
        (res) => {
          this.s = res.data || this.s;
          this.busy = false;
        },
        (err) => {
          this.message = (err.response && err.response.data && err.response.data.error)
            || 'Det skjedde en feil. Prøv igjen.';
          this.busy = false;
        }
      );
    },
    // Same format as Aktiver VIVA-betaling: 13.10.2026 09:45, whatever the ICU version.
    formatTime(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const pad = (n) => String(n).padStart(2, '0');
      return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear()
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    }
  }
};
</script>

<style scoped>
.tp-card { border: 1px dashed var(--bo-line); border-radius: 10px; padding: 12px 14px; background: var(--bo-surface); }
.tp-head { display: flex; align-items: baseline; gap: 0.4rem; flex-wrap: wrap; }
.tp-table { font-size: 0.9rem; }
.tp-table td { padding: 2px 12px 2px 0; vertical-align: top; }
.tp-label { color: var(--bo-ink-2); white-space: nowrap; }
.tp-muted { color: var(--bo-muted); }
.tp-ok { color: var(--bo-success-ink); font-weight: 600; }
.tp-warn { font-size: 0.85rem; color: var(--bo-alert-ink); }
.tp-note { font-size: 0.85rem; color: var(--bo-ink-2); line-height: 1.5; }
</style>
